import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  acceptFireIncident,
  closeFireIncident,
  fetchFireIncidents,
} from '../api/fire';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { StatusDot } from '../components/dashboard/schoolEquipmentChrome';
import { surface } from '../components/ui/surface';

const statusTone = {
  open: 'critical',
  accepted: 'warning',
  closed: 'muted',
};

const statusBadge = (status, t) => (
  <StatusDot tone={statusTone[status] || 'muted'} label={t(`fire.status.${status}`)} />
);

const quietButton = 'inline-flex items-center gap-2 rounded-xl bg-white/[0.06] px-4 py-2.5 text-sm font-medium text-muted-100 transition hover:bg-white/[0.1] hover:text-white disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400';

const FireIncidents = ({ setIsAuthenticated, userEmail, userRole, canRespondToIncidents, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSidebarVisible, setSidebarVisible] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [resolution, setResolution] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const toggleSidebar = () => setSidebarVisible((prev) => !prev);
  const canMutate = Boolean(canRespondToIncidents);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await fetchFireIncidents(params);
      setIncidents(Array.isArray(data) ? data : []);
    } catch {
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    const onUpdate = () => load();
    window.addEventListener('fireIncidentUpdated', onUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener('fireIncidentUpdated', onUpdate);
    };
  }, [load]);

  const selected = incidents.find((i) => i.id === selectedId) || null;
  const active = incidents.filter((i) => i.status !== 'closed');
  const history = incidents.filter((i) => i.status === 'closed');

  const handleAccept = async (id) => {
    setActionLoading(true);
    try {
      await acceptFireIncident(id);
      window.dispatchEvent(new CustomEvent('fireIncidentUpdated', { detail: { id, status: 'accepted' } }));
      await load();
    } catch (e) {
      const data = e?.response?.data;
      alert(data?.message || t('fire.alreadyAccepted'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = async () => {
    if (!selected || !resolution.trim()) return;
    setActionLoading(true);
    try {
      await closeFireIncident(selected.id, resolution.trim());
      window.dispatchEvent(new CustomEvent('fireIncidentUpdated', { detail: { id: selected.id, status: 'closed' } }));
      setResolution('');
      setSelectedId(null);
      await load();
    } catch {
      alert(t('fire.closeFailed', { defaultValue: 'Не удалось закрыть инцидент' }));
    } finally {
      setActionLoading(false);
    }
  };

  const goToSchool = (incident) => {
    const schoolIp = incident?.school_ip;
    if (!schoolIp) return;
    navigate(`/schools/${encodeURIComponent(schoolIp)}`);
  };

  const goToVideo = (incident) => {
    const schoolIp = incident?.school_ip;
    const deviceId = incident?.primary_device_id || incident?.device_id;
    if (!schoolIp || !deviceId) return;
    navigate(`/schools/${encodeURIComponent(schoolIp)}/nvr/${deviceId}`);
  };

  const renderRow = (incident) => (
    <button
      key={incident.id}
      type="button"
      onClick={() => setSelectedId(incident.id)}
      className={`flex w-full items-center gap-4 rounded-2xl px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
        selectedId === incident.id
          ? 'bg-white/[0.1]'
          : `${surface.card} hover:bg-white/[0.06]`
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-white">{incident.school}</p>
        <p className="text-xs text-muted-400">
          {[incident.nvr_name, incident.region].filter(Boolean).join(' · ')}
        </p>
      </div>
      {statusBadge(incident.status, t)}
    </button>
  );

  return (
    <div className="relative flex min-h-screen flex-col overflow-clip bg-surface-base text-muted-100">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-brand-radial opacity-50" />
      <Header isSidebarVisible={isSidebarVisible} toggleSidebar={toggleSidebar} userEmail={userEmail} userRole={userRole} />
      <div className="flex flex-1">
        <Sidebar onLogout={onLogout} userRole={userRole} isSidebarVisible={isSidebarVisible} onClose={() => setSidebarVisible(false)} />
        <main className="flex min-w-0 flex-1 flex-col gap-6 bg-surface-base/60 px-3 py-4 sm:px-4 sm:py-6 lg:px-10">
          <div className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="font-display text-xl text-white md:text-2xl">{t('fire.title')}</h1>
                <p className="mt-1 text-sm text-muted-400">{t('fire.subtitle')}</p>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 rounded-xl bg-white/[0.04] px-3 text-sm text-muted-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              >
                <option value="">{t('fire.allStatuses')}</option>
                <option value="open">{t('fire.status.open')}</option>
                <option value="accepted">{t('fire.status.accepted')}</option>
                <option value="closed">{t('fire.status.closed')}</option>
              </select>
            </div>

            {loading && <p className="text-sm text-muted-400">{t('common.loading')}</p>}

            <section className="space-y-3">
              <h2 className="font-display text-lg text-white">{t('fire.active')}</h2>
              {active.length === 0 ? (
                <p className="text-sm text-muted-400">{t('fire.noActive')}</p>
              ) : (
                <div className="grid gap-3 lg:grid-cols-2">{active.map(renderRow)}</div>
              )}
            </section>

            {selected && (
              <div className={`space-y-4 rounded-3xl p-6 ${surface.section}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-display text-lg text-white">
                    {selected.school || t('fire.unknownSchool')}
                  </h3>
                  {statusBadge(selected.status, t)}
                </div>
                <p className="text-sm text-muted-400">
                  {[selected.nvr_name, selected.region, selected.city].filter(Boolean).join(' · ')}
                </p>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={!selected.school_ip || !(selected.primary_device_id || selected.device_id)}
                    onClick={() => goToVideo(selected)}
                    className={quietButton}
                  >
                    <span className="material-icons text-base">videocam</span>
                    {t('fire.goToVideo')}
                  </button>
                  <button
                    type="button"
                    disabled={!selected.school_ip}
                    onClick={() => goToSchool(selected)}
                    className={quietButton}
                  >
                    <span className="material-icons text-base">school</span>
                    {t('fire.goToSchool')}
                  </button>
                  {canMutate && selected.status === 'open' && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => handleAccept(selected.id)}
                      className="inline-flex items-center rounded-xl bg-critical/15 px-4 py-2.5 text-sm font-medium text-critical transition hover:bg-critical/25 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    >
                      {t('fire.accept')}
                    </button>
                  )}
                </div>

                {canMutate && selected.status !== 'closed' && (
                  <div className="space-y-2">
                    <textarea
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                      placeholder={t('fire.resolutionPlaceholder')}
                      rows={3}
                      className="w-full rounded-xl bg-white/[0.04] p-3 text-sm text-white outline-none placeholder:text-muted-500 focus-visible:ring-2 focus-visible:ring-brand-400"
                    />
                    <button
                      type="button"
                      disabled={actionLoading || !resolution.trim() || selected.status === 'open'}
                      onClick={handleClose}
                      className={quietButton}
                      title={selected.status === 'open' ? t('fire.acceptFirst') : undefined}
                    >
                      {t('fire.close')}
                    </button>
                  </div>
                )}

                {selected.resolution && (
                  <p className="text-sm text-muted-300">
                    <strong>{t('fire.resolution')}:</strong> {selected.resolution}
                  </p>
                )}
              </div>
            )}

            <section className="space-y-3">
              <h2 className="font-display text-lg text-white">{t('fire.history')}</h2>
              {history.length === 0 ? (
                <p className="text-sm text-muted-400">{t('fire.noHistory')}</p>
              ) : (
                <div className="grid gap-3 lg:grid-cols-2">{history.slice(0, 20).map(renderRow)}</div>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default FireIncidents;
